import { Avatar, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { MdOutlineMailOutline, MdOutlinePhone } from 'react-icons/md'

import { EntityHeader, HeaderChip } from '@/Ship/Components/EntityCard'
import { initials } from '@/Ship/Helpers/fieldViews'
import { formatPhone } from '@/Ship/Helpers/formatPhone'

import { USER_BASE_ROLE, USER_STATUS_OPTIONS, assignedRoles, roleLabel } from '../Contracts/UserOptions'

import type { UserFormContextLike } from '../Contracts/IUser'

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

/**
 * Шапка співробітника — окремим observer-компонентом: вона читає поля, які
 * редагують, а ядровий `FormField` на кожне перемальовування шаблону
 * перемонтовує всі поля форми (`SPEC-002` §4.2).
 *
 * Ліворуч — хто це: аватар, ПІБ, посада, стан і ролі (без жодної —
 * «Співробітник»). Праворуч — як зв'язатися: мобільний і пошта, клік копіює.
 */
export const UserCardHeader = observer(({ formContext }: { formContext: UserFormContextLike }) => {
    const { token } = theme.useToken()
    const values = formContext.values ?? {}

    const fullName =
        text(values['fullName']) ||
        [values['lastName'], values['firstName'], values['middleName']].map(text).filter(Boolean).join(' ')
    // Ініціали — з короткого імені: з повного вийшли б не ті літери
    const shortName = text(values['shortName']) || fullName
    const position = text(values['position'])
    const phone = text(values['phone'])
    const email = text(values['email'])
    const photo = values['photo'] as { url?: unknown } | null | undefined
    const photoUrl = typeof photo?.url === 'string' && photo.url !== '' ? photo.url : undefined

    const status = USER_STATUS_OPTIONS.find((option) => option.value === values.status)
    const roles = assignedRoles(values.roles).map(roleLabel)
    const badges = (roles.length > 0 ? roles : [USER_BASE_ROLE.label]).map((label) => (
        <HeaderChip key={label}>{label}</HeaderChip>
    ))

    const contacts =
        phone || email ? (
            <>
                {phone ? (
                    <HeaderChip icon={<MdOutlinePhone />} copyText={formatPhone(phone)}>
                        {formatPhone(phone)}
                    </HeaderChip>
                ) : null}
                {email ? (
                    <HeaderChip icon={<MdOutlineMailOutline />} copyText={email}>
                        {email}
                    </HeaderChip>
                ) : null}
            </>
        ) : undefined

    return (
        <EntityHeader
            status={status ? { label: status.label, color: status.color } : undefined}
            badges={badges}
            aside={contacts}
            title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                    <Avatar
                        size={40}
                        {...(photoUrl ? { src: photoUrl } : {})}
                        style={{ background: token.colorPrimaryBg, color: token.colorPrimary, fontSize: 15, flexShrink: 0 }}
                    >
                        {initials(shortName)}
                    </Avatar>
                    {fullName}
                </span>
            }
            {...(position ? { subtitle: position } : {})}
        />
    )
})
